import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex, Spin, message } from 'antd';

import { UserAuth } from '../../../middleware/UserContext/UserContext.middleware';
import { useTranslation } from 'react-i18next';
import { useAdvertChannels } from '../../hooks/useAdvertChannels';
import { useAdvertDetails } from '../../hooks/useAdvertDetails';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useAdvertTelegramAccounts } from '../../hooks/useAdvertTelegramAccounts';
import AdvertWizard from '../../components/AdvertWizard/AdvertWizard.component';

type TAdvertEditPageProps = {
  advertUUID: string;
};

const AdvertEditPage: React.FC<TAdvertEditPageProps> = ({ advertUUID }) => {
  const { currentUser } = UserAuth();
  const { channelUUIDs, loading: advertChannelsLoading } = useAdvertChannels(currentUser, advertUUID);
  const [checkedChannelsList, setCheckedChannelsList] = useState<Record<string, boolean>>({});
  
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { isAdmin, isResolved } = useCurrentUser();
  const [checkedTelegramAccounts, setCheckedTelegramAccounts] = useState<Record<string, boolean>>({});
  const [newCheckedChannels, setNewCheckedChannels] = useState<Record<string, boolean>>({});
  const [uploadQueue, setUploadQueue] = useState<{ file: File; onSuccess: () => void }[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const modern = useAdvertDetails(currentUser, advertUUID);
  const advertModel = modern.advert;
  const advertModelLoading = modern.loading;
  const uploadFile = modern.uploadFile;
  const removeAttachment = modern.removeAttachment;
  const setTitle = modern.setTitle;
  const setDescription = modern.setDescription;
  const reorderAttachments = modern.reorderAttachments;
  const submitAdvert = modern.submitAdvert;

  useEffect(() => {
    if (channelUUIDs.length) {
      const rec: Record<string, boolean> = {};
      channelUUIDs.forEach(id => { rec[id] = true; });
      setCheckedChannelsList(rec);
    }
  }, [channelUUIDs]);

  useEffect(() => { setNewCheckedChannels(checkedChannelsList); }, [checkedChannelsList]);

  const { telegramAccountUUIDs, loading: telegramAccountsLoading, error: telegramAccountsError } = useAdvertTelegramAccounts(advertUUID, isAdmin);

  useEffect(() => {
    if (telegramAccountsError) {
      message.error(t('advert-telegram-accounts-load-failed', { ns: 'errors' }));
    }
  }, [telegramAccountsError, t]);

  useEffect(() => {
    setCheckedTelegramAccounts(Object.fromEntries(telegramAccountUUIDs.map(id => [id, true])));
  }, [telegramAccountUUIDs]);

  

  useEffect(() => {
    const processQueue = async () => {
      if (uploadQueue.length > 0 && !isUploading) {
        setIsUploading(true);
        const { file, onSuccess } = uploadQueue[0];
        
        try {
          await uploadFile(file, (advertModel?.attachments?.length || 0));
          onSuccess();
        } catch (error: any) {
          const key = error?.message || 'upload-error';
          message.error(t(key, { ns: 'errors', limit: 10 }));
        } finally {
          setIsUploading(false);
          setUploadQueue(prevQueue => prevQueue.slice(1));
        }
      }
    };

    processQueue();
  }, [uploadQueue, isUploading, uploadFile, advertModel?.attachments?.length]);

  const uploadHandler = ({ file, onSuccess }: { file: File; onSuccess: () => void }) => {
    setUploadQueue(prevQueue => [...prevQueue, { file, onSuccess }]);
  };

  const submitHandler = async () => {
    if (!currentUser || !advertModel) {
      return navigate('/login');
    }

    if (isAdmin && telegramAccountsLoading) {
      message.warning(t('advert-telegram-accounts-loading', { ns: 'errors' }));
      return;
    }

    if (isAdmin && telegramAccountsError) {
      message.error(t('advert-telegram-accounts-load-failed', { ns: 'errors' }));
      return;
    }

    setIsSubmitting(true);
    const selectedChannelUUIDs = Object.keys(newCheckedChannels).filter(key => newCheckedChannels[key]);

    const selectedTelegramAccountUUIDs = Object.keys(checkedTelegramAccounts).filter(key => checkedTelegramAccounts[key]);
    const telegramAccountsChanged = selectedTelegramAccountUUIDs.length !== telegramAccountUUIDs.length
      || selectedTelegramAccountUUIDs.some(id => !telegramAccountUUIDs.includes(id));
    const sendTelegramAccounts = isAdmin && telegramAccountsChanged && selectedTelegramAccountUUIDs.length > 0;

    try {
      await submitAdvert(selectedChannelUUIDs, sendTelegramAccounts ? selectedTelegramAccountUUIDs : undefined);
      navigate('/adverts');
    } catch (err: any) {
      const key = err?.message || 'advert-update-failed';
      message.error(t(key, { ns: 'errors', limit: 10 }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isResolved) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <AdvertWizard
      advertModel={advertModel}
      isAdvertLoading={advertModelLoading}
      checkedChannels={newCheckedChannels}
      isSubmitting={isSubmitting}
      pageTitle={t('advert-edit-title', { ns: 'labels' })}
      submitButtonText={t('save', { ns: 'buttons' })}
      onTitleChange={setTitle}
      onDescriptionChange={setDescription}
      onUploadFile={uploadHandler}
      onRemoveAttachment={removeAttachment}
      onReorderAttachments={reorderAttachments}
      showTelegramAccountsStep={isAdmin}
      checkedTelegramAccounts={checkedTelegramAccounts}
      onCheckTelegramAccounts={setCheckedTelegramAccounts}
      onCheckChannels={setNewCheckedChannels}
      onSubmit={submitHandler}
    />
  );
};

export default AdvertEditPage;